import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";

const GestionEspacios = () => {
  const navigate = useNavigate();

  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState(false);
  const [espacioId, setEspacioId] = useState(null);

  const [mostrarModalEliminar, setMostrarModalEliminar] = useState(false);
  const [espacioIdParaEliminar, setEspacioIdParaEliminar] = useState(null);

  const [alerta, setAlerta] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success"
  });

  // Estado del formulario
  const [nuevoEspacio, setNuevoEspacio] = useState({
    nombre: "",
    descripcion: "",
    permite_evento: false,
  });

  // Estados para Cloudinary (imagen)
  const [imagenFile, setImagenFile] = useState(null);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const mostrarAlerta = (mensaje, tipo = "success") => {
    setAlerta({
      mostrar: true,
      mensaje,
      tipo
    });
    setTimeout(() => {
      setAlerta({ mostrar: false, mensaje: "", tipo: "success" });
    }, 3000);
  };

  // Cargar espacios
  const cargarEspacios = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/espacios");
      setEspacios(response.data.espacios || []);
    } catch (error) {
      console.error("Error al cargar espacios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarEspacios();
  }, []);

  // ==================== DRAG & DROP ====================
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    } else {
      mostrarAlerta("Solo se permiten imágenes", "danger");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const processFile = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      mostrarAlerta("La imagen no debe superar los 5MB", "danger");
      return;
    }
    setImagenFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagenPreview(previewUrl);
  };

  const removeImagen = () => {
    setImagenFile(null);
    if (imagenPreview) URL.revokeObjectURL(imagenPreview);
    setImagenPreview(null);
  };

  // ==================== GUARDAR ESPACIO ====================
  const guardarEspacio = async () => {
    if (!nuevoEspacio.nombre.trim() || !nuevoEspacio.descripcion.trim()) {
      mostrarAlerta("Nombre y descripción son obligatorios", "danger");
      return;
    }

    const formData = new FormData();
    formData.append("nombre", nuevoEspacio.nombre.trim());
    formData.append("descripcion", nuevoEspacio.descripcion.trim());
    formData.append("permite_evento", nuevoEspacio.permite_evento);

    if (imagenFile) {
      formData.append("imagen", imagenFile); // ← nombre que espera multer en backend
    }

    const url = editando ? `/espacios/${espacioId}` : "/espacios";
    const method = editando ? "put" : "post";

    try {
      await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 minutos
      });

      setMostrarModal(false);
      resetForm();
      mostrarAlerta(editando ? "Espacio actualizado correctamente" : "Espacio creado correctamente");
      cargarEspacios();
    } catch (error) {
      console.error("Error al guardar espacio:", error);
      mostrarAlerta(error.response?.data?.message || "Error al guardar el espacio", "danger");
    }
  };

  const resetForm = () => {
    setEditando(false);
    setEspacioId(null);
    setNuevoEspacio({
      nombre: "",
      descripcion: "",
      permite_evento: false,
    });
    setImagenFile(null);
    if (imagenPreview) URL.revokeObjectURL(imagenPreview);
    setImagenPreview(null);
  };

  // Preparar edición
  const abrirEditar = (espacio) => {
    setNuevoEspacio({
      nombre: espacio.nombre,
      descripcion: espacio.descripcion,
      permite_evento: Boolean(espacio.permite_evento),
    });
    setEspacioId(espacio.id);
    setEditando(true);
    setImagenFile(null);
    setImagenPreview(espacio.imagen || null);
    setMostrarModal(true);
  };

  // Eliminar
  const abrirModalEliminar = (id) => {
    setEspacioIdParaEliminar(id);
    setMostrarModalEliminar(true);
  };

  const confirmarEliminar = async () => {
    try {
      await axios.delete(`/espacios/${espacioIdParaEliminar}`);
      setEspacios((prev) => prev.filter((e) => e.id !== espacioIdParaEliminar));
      setMostrarModalEliminar(false);
      setEspacioIdParaEliminar(null);
      mostrarAlerta("Espacio eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar espacio:", error);
      mostrarAlerta("No se pudo eliminar el espacio", "danger");
    }
  };

  return (
    <div className="container-fluid bg-light min-vh-100 py-4 px-3 px-md-4 px-lg-5">

      {/* Alerta */}
      {alerta.mostrar && (
        <div
          className={`alert alert-${alerta.tipo} alert-dismissible fade show shadow position-fixed`}
          style={{ top: "20px", right: "20px", zIndex: 9999, minWidth: "320px" }}
        >
          <div className="d-flex align-items-center">
            <i className={`bi me-2 ${alerta.tipo === "success" ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
            <span>{alerta.mensaje}</span>
          </div>
          <button className="btn-close" onClick={() => setAlerta({ ...alerta, mostrar: false })}></button>
        </div>
      )}

      {/* Header */}
      <div className="card shadow-sm border-0 mb-4 border-start border-success border-4 rounded-3">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-success border-0 rounded-circle p-2"
              onClick={() => navigate("/DashboardSecretaria")}
              title="Volver al dashboard"
            >
              <i className="bi bi-arrow-left-short fs-4"></i>
            </button>
            <h4 className="fw-bold mb-0 text-dark">
              <i className="bi bi-geo-alt-fill me-2 text-success"></i>
              Gestión de Espacios
            </h4>
          </div>

          <button
            className="btn btn-success d-flex align-items-center gap-2 shadow-sm px-3"
            onClick={() => {
              resetForm();
              setMostrarModal(true);
            }}
          >
            <i className="bi bi-plus-lg"></i>
            Nuevo Espacio
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : espacios.length === 0 ? (
        <div className="card border-0 shadow-sm text-center p-5">
          <i className="bi bi-geo-alt fs-1 text-success opacity-50 mb-3"></i>
          <h5 className="text-muted">No hay espacios registrados</h5>
          <p className="text-muted small">Agrega un nuevo espacio para comenzar</p>
        </div>
      ) : (
        <div className="row g-4">
          {espacios.map((espacio) => (
            <div className="col-lg-4 col-md-6" key={espacio.id}>
              <div className="card shadow-sm border-0 rounded-3 h-100 overflow-hidden">
                {espacio.imagen ? (
                  <img
                    src={espacio.imagen}
                    alt={espacio.nombre}
                    className="card-img-top"
                    style={{ height: "160px", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/400x160?text=Espacio";
                    }}
                  />
                ) : (
                  <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: "160px" }}>
                    <i className="bi bi-geo-alt fs-1 text-muted"></i>
                  </div>
                )}

                <div className="card-body d-flex flex-column p-3">
                  <h5 className="card-title fw-bold mb-2">{espacio.nombre}</h5>
                  <p className="text-muted small mb-3 flex-grow-1">
                    {espacio.descripcion || "Sin descripción"}
                  </p>

                  <div className="mt-auto">
                    <span className={`badge px-3 py-2 ${espacio.permite_evento ? "bg-success" : "bg-secondary"}`}>
                      {espacio.permite_evento ? "Permite eventos" : "Uso normal"}
                    </span>
                  </div>
                </div>

                <div className="card-footer bg-white border-0 p-3 d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => abrirEditar(espacio)}
                    title="Editar"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => abrirModalEliminar(espacio.id)}
                    title="Eliminar"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==================== MODAL CREAR / EDITAR ==================== */}
      {mostrarModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 shadow border-0">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-success">
                  {editando ? "Editar Espacio" : "Nuevo Espacio"}
                </h5>
                <button type="button" className="btn-close" onClick={() => { setMostrarModal(false); resetForm(); }}></button>
              </div>

              <div className="modal-body px-4 pb-4">
                <div className="mb-3">
                  <label className="form-label fw-medium">Nombre del espacio</label>
                  <input
                    className="form-control"
                    placeholder="Ej: Auditorio Principal"
                    value={nuevoEspacio.nombre}
                    onChange={(e) => setNuevoEspacio({ ...nuevoEspacio, nombre: e.target.value })}
                    autoFocus
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-medium">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Capacidad, ubicación, características especiales..."
                    value={nuevoEspacio.descripcion}
                    onChange={(e) => setNuevoEspacio({ ...nuevoEspacio, descripcion: e.target.value })}
                  />
                </div>

                {/* Drag & Drop Imagen */}
                <div className="mb-4">
                  <label className="form-label fw-medium">Imagen del espacio</label>
                  <div
                    className={`border border-2 border-dashed rounded-3 p-4 text-center ${isDragging ? "border-success bg-success bg-opacity-10" : "border-secondary"}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById("imagenInput").click()}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="file"
                      id="imagenInput"
                      accept="image/*"
                      className="d-none"
                      onChange={handleFileSelect}
                    />

                    {imagenPreview ? (
                      <div className="position-relative d-inline-block">
                        <img
                          src={imagenPreview}
                          alt="Vista previa"
                          className="img-fluid rounded shadow-sm"
                          style={{ maxHeight: "180px", objectFit: "contain" }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                          onClick={removeImagen}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ) : (
                      <div>
                        <i className="bi bi-cloud-upload fs-1 text-muted mb-2 d-block"></i>
                        <p className="mb-1 fw-medium">Arrastra tu imagen aquí o haz clic para seleccionar</p>
                        <small className="text-muted">PNG, JPG, WEBP (máx. 5MB)</small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-check mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="permiteEvento"
                    checked={nuevoEspacio.permite_evento}
                    onChange={(e) => setNuevoEspacio({ ...nuevoEspacio, permite_evento: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="permiteEvento">
                    Este espacio permite la realización de eventos
                  </label>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4">
                <button className="btn btn-outline-secondary px-4" onClick={() => { setMostrarModal(false); resetForm(); }}>
                  Cancelar
                </button>
                <button className="btn btn-success px-4 d-flex align-items-center gap-2" onClick={guardarEspacio}>
                  <i className="bi bi-check-lg"></i>
                  {editando ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      {mostrarModalEliminar && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-3 shadow border-0">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title fw-bold">Eliminar Espacio</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setMostrarModalEliminar(false)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">¿Estás seguro de que deseas <strong>eliminar</strong> este espacio?</p>
                <p className="text-muted small mb-0">Esta acción no se puede deshacer.</p>
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-outline-secondary px-4" onClick={() => setMostrarModalEliminar(false)}>
                  Cancelar
                </button>
                <button className="btn btn-danger px-4" onClick={confirmarEliminar}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionEspacios;